//--- React
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
//--- Ant Design
import { Button, Card, Col, Divider, Row, Spin } from "antd";
//--- Project
import APIError from "components/Common/APIError";
import TreeMap from "components/Common/Charts/TreeMap";
import {
  getCompanyReviewCounts,
  getCompanyReviewPercentages,
} from "apis/competitorPulseApis";
//--- Icon
import { NegativeIcon, NeutralIcon, PositiveIcon, ReviewIcon } from "../icon";
import { color } from "highcharts";
import { primaryColor, secondaryColor, treeMapOtherColor } from "../constants";

const otherCompaniesBelowValue = 1

const CompanyWiseSpread = ({ selectedColor, onChangeTab,companiesList,companyDataLoading,companyDataIsError,companyDataError }) => {
  const [filterValue, setFilterValue] = useState({});
  const [companyReview, setCompanyReview] = useState({
    data: [],
    drilldownSeries: {},
  });

  const onFilterChange = (name, value) => {
    setFilterValue((prev) => ({
      [name]: prev?.[name] === value ? null : value,
    }));
  };

  useEffect(() => {
    if (companiesList?.companies) {
      const data = Object.entries(companiesList.companies).map(
        ([key, value], index) => ({
          id: index + 1,
          parent: 1,
          name: key,
          value: key === "Other" ? otherCompaniesBelowValue : value,
          drilldown: key === "Other" ? key : null,
          color: key === "Other" ?  treeMapOtherColor :primaryColor,
        })
      );
      const drilldowndata = Object.entries(
        companiesList.companies?.Other?.companies || {}
      ).map(([key, value], index) => ({
        id: index + 10,
        parent: 2,
        name: key,
        value: key === "Other" ? otherCompaniesBelowValue : value,
        color:primaryColor,
      }));
      setCompanyReview({
        data,
        drilldownSeries: {
          id: "Other",
          type: "treemap",
          data: drilldowndata,
        },
      });
    }
  }, [companiesList, selectedColor]);
  
  

  const {
    data: reviewTypeData,
    isLoading: reviewTypeDataLoading,
    isError: reviewTypeDataIsError,
    error: reviewTypeDataError,
  } = useQuery({
    queryKey: ["ReviewCounts", filterValue.company],
    queryFn: () => getCompanyReviewCounts(filterValue.company),
    keepPreviousData: true,
    enabled: !!filterValue.company,
    refetchOnWindowFocus: false,
  });

  return (
    <Card
      title={<>
      <div className="sub-header">Company Wise Spread</div>
      <div className="card-sub-text">
Online user engagement ordered by company.
    </div>
      </>
    }
      bordered={false}
    >
      {companyDataIsError ? (
        <Row className="tree-map ml1">
          <APIError message={companyDataError?.response?.data?.message} />
        </Row>
      ) : companyDataLoading ? (
        <div className="tree-map flex-center">
          <Spin tip="Loading" size="large" />
        </div>
      ) : (
        <Row className="tree-map">
          <Col
            xs={24}
            sm={24}
            md={filterValue.company ? 13 : 24}
            lg={filterValue.company ? 13 : 24}
            xl={filterValue.company ? 13 : 24}
          >
            <Row justify={"center"} align={"middle"} className="plr05">
              <Col xs={24} sm={24}>
                <TreeMap
                  treeData={companyReview?.data}
                  drilldownSeries={companyReview?.drilldownSeries}
                  chartTitle=""
                  extraMargin={filterValue.company ? null : 0}
                  onClick={(value) => onFilterChange("company", value)}
                  chartColor={primaryColor}
                  selectedPointColor={secondaryColor}
                  selectedCompanyName={filterValue.company}
                  onDoubleClick={(value) =>
                    onChangeTab("detailed-insights", value)
                  }
                />
              </Col>
            </Row>
          </Col>
          <Col
            xs={0}
            sm={0}
            md={filterValue.company ? 1 : 0}
            lg={filterValue.company ? 1 : 0}
            xl={filterValue.company ? 1 : 0}
            className="h100vh"
          >
            <div className="flex-center h100">
              <Divider type="vertical" className="h100" orientation="right" />
            </div>
          </Col>
          <Col
            xs={filterValue.company ? 24 : 0}
            sm={filterValue.company ? 24 : 0}
            md={filterValue.company ? 10 : 0}
            lg={filterValue.company ? 10 : 0}
            xl={filterValue.company ? 10 : 0}
          >
            <Row justify={"center"} align={"middle"} className="h100 plr05">
              <Col xs={24} md={24} className="h100">
                {reviewTypeDataIsError ? (
                  <APIError
                    message={reviewTypeDataError?.response?.data?.message}
                  />
                ) : reviewTypeDataLoading ? (
                  <Row justify={"center"} align={"middle"} className="h100">
                    <Spin tip="Loading" size="large" />
                  </Row>
                ) : (
                  <div className="text-center mt3 company-review-box">
                    <div className="sub-header">What are customers saying?</div>
                    <Row gutter={[16, 16]} className="mt2">
                      <Col span={12}>
                        <Row gutter={[16, 16]} className="flex-center">
                          <Col xs={10} md={8} lg={6}>
                            <ReviewIcon className="overall-review-icon review-icon" />
                          </Col>
                          <Col span={8}>
                            <div className="count">
                              {reviewTypeData?.total_reviews}
                            </div>
                            <div className="grey">Total</div>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row gutter={[16, 16]} className="flex-center">
                          <Col xs={10} md={8} lg={6}>
                            <PositiveIcon className="positive-review-icon review-icon" />
                          </Col>
                          <Col span={8}>
                            <div className="count">
                              {reviewTypeData?.positive_count}
                            </div>
                            <div className="grey">Positive</div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} className="mt2">
                      <Col span={12}>
                        <Row gutter={[16, 16]} className="flex-center">
                          <Col xs={10} md={8} lg={6}>
                            <NegativeIcon className="negative-review-icon review-icon" />
                          </Col>
                          <Col span={8}>
                            <div className="count">
                              {reviewTypeData?.negative_count}
                            </div>
                            <div className="grey">Negative</div>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row gutter={[16, 16]} className="flex-center">
                          <Col xs={10} md={8} lg={6}>
                            <NeutralIcon className="neutral-review-icon review-icon" />
                          </Col>
                          <Col span={8}>
                            <div className="count">
                              {reviewTypeData?.neutral_count}
                            </div>
                            <div className="grey">Neutral</div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                )}
              </Col>
            </Row>
          </Col>
          <Col span={24} className="flex-end mt05">
            <Button
              type="primary"
              className="view-feedback-btn pd1"
              htmlType="submit"
              size="large"
              onClick={() =>
                onChangeTab("detailed-insights", filterValue.company)
              }
            >
              View Detailed Insights
            </Button>
          </Col>
        </Row>
      )}
    </Card>
  );
};

export default CompanyWiseSpread;
